/**
 * Business of the Month — pure helpers for the admin console (page.tsx,
 * NominationsTab.tsx, EligibilityTab.tsx). Kept dependency-free so the grouping,
 * state, and scoring math that drives the console is covered directly by
 * botm.test.ts rather than only indirectly through component tests — the same
 * shape as businesses/decision.ts.
 *
 * The 5th-of-the-month cutoff, month formatting, and share copy are NOT
 * reimplemented here: they live once in @muaa/shared (shared/src/jaguarOwned.ts,
 * built in mobile M1) so the client that submits a nomination and the admin
 * console that schedules the winner always agree on which month is which.
 */
import { nominationTargetMonth, spotlightMonthEnded, monthLabel, spotlightShareText } from '@muaa/shared'
export { nominationTargetMonth, monthLabel, spotlightShareText }

/**
 * The next `count` first-of-month dates starting from `from` (inclusive), for
 * the month picker. `from` must be a `'YYYY-MM-01'` string (any day-of-month is
 * tolerated but ignored — only the year/month are read).
 */
export function upcomingMonths(from: string, count: number): string[] {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(from)
  if (!m) return []
  const year = Number(m[1])
  const month = Number(m[2])
  const result: string[] = []
  for (let i = 0; i < count; i++) {
    // Date's month is 0-indexed and rolls over years for us (mirrors
    // firstOfMonthAfter in shared/src/jaguarOwned.ts).
    const d = new Date(Date.UTC(year, month - 1 + i, 1))
    const y = d.getUTCFullYear()
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
    result.push(`${y}-${mo}-01`)
  }
  return result
}

/** `'YYYY-MM-01'` in, `'YYYY-MM-01'` out, `n` months later (negative = earlier),
 * UTC arithmetic (mirrors `firstOfMonthAfter` in shared/src/jaguarOwned.ts). */
export function addMonths(month: string, n: number): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(month)
  if (!m) return month
  const year = Number(m[1])
  const idx = Number(m[2]) - 1 // 0-indexed month of `month` itself
  const d = new Date(Date.UTC(year, idx + n, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
}

/**
 * The month the committee is actively working — the one Storyboard F3 opens
 * the console on ("October 2026 · nominations closed September 5"), NOT the
 * still-open `nominationTargetMonth` (nominations for that later month are
 * still coming in while the committee is scoring and scheduling this one).
 *
 * `nominationTargetMonth(now)` is always either 1 or 2 months out (see its own
 * doc comment). Two months out means the month one-before-target hasn't ended
 * yet — the committee is still working IT, so the spotlight month is
 * `target - 1`. One month out (right after the 5th rolls the window forward)
 * means the month two-before-target has already ended — the committee has
 * moved on to `target` itself. Built entirely from existing @muaa/shared
 * helpers, so the America/New_York calendar still has exactly one
 * implementation.
 */
export function upcomingSpotlightMonth(now: Date = new Date()): string {
  const t = nominationTargetMonth(now)
  return spotlightMonthEnded(addMonths(t, -2), now) ? t : addMonths(t, -1)
}

export interface NominationRow {
  id: string
  business_id: string
  month: string
  reason: string
  is_self: boolean
  created_at: string
  business?: { id: string; name: string } | null
  nominator?: { name: string | null; class_year: string | null } | null
}

export interface NominationGroup {
  businessId: string
  businessName: string
  count: number
  nominators: string[]
  latestReason: string
  hasSelf: boolean
}

/** Group nominations by business, most nominated first, then by name. */
export function groupNominations(rows: NominationRow[]): NominationGroup[] {
  const order: string[] = []
  const byBusiness = new Map<string, { businessName: string; rows: NominationRow[] }>()
  for (const row of rows) {
    const key = row.business_id
    let bucket = byBusiness.get(key)
    if (!bucket) {
      bucket = { businessName: row.business?.name ?? 'Unknown business', rows: [] }
      byBusiness.set(key, bucket)
      order.push(key)
    }
    bucket.rows.push(row)
  }

  const groups: NominationGroup[] = order.map((businessId) => {
    const { businessName, rows: groupRows } = byBusiness.get(businessId)!
    // Sort ascending by created_at (an ISO string, so lexicographic order
    // matches chronological order) BEFORE reading off nominators, so the "first
    // 2 names" a caller shows are stably the earliest nominators — not
    // whatever order the query happened to return.
    const sorted = [...groupRows].sort((a, b) => a.created_at.localeCompare(b.created_at))
    const latest = sorted[sorted.length - 1]
    return {
      businessId,
      businessName,
      count: sorted.length,
      nominators: sorted.map((r) => r.nominator?.name ?? 'Unnamed member'),
      latestReason: latest.reason,
      hasSelf: sorted.some((r) => r.is_self),
    }
  })

  groups.sort((a, b) => b.count - a.count || a.businessName.localeCompare(b.businessName))
  return groups
}

export type SpotlightState = 'draft' | 'scheduled' | 'live' | 'past'

/**
 * draft = no published_at; scheduled = future published_at; live = published
 * and month not yet over; past = published and the month has ended.
 *
 * Reuses `spotlightMonthEnded` (@muaa/shared) for the live/past boundary rather
 * than a second America/New_York comparison — that function already mirrors
 * `get_spotlight_report`'s own timezone pin.
 */
export function spotlightState(publishedAt: string | null, month: string, now: Date): SpotlightState {
  if (!publishedAt) return 'draft'
  const pub = new Date(publishedAt)
  if (Number.isNaN(pub.getTime()) || pub > now) return 'scheduled'
  return spotlightMonthEnded(month, now) ? 'past' : 'live'
}

export interface ScoreRow {
  id: string
  month: string
  business_id: string
  scorer_user_id: string
  recused: boolean
  recusal_reason: string | null
  story: number | null
  vouches: number | null
  rotation: number | null
  listing: number | null
  first_time: number | null
  total: number
}

/** The published 100-point rubric, in display order. */
export const RUBRIC: { key: 'story' | 'vouches' | 'rotation' | 'listing' | 'first_time'; label: string; max: number }[] = [
  { key: 'story', label: 'Community & service story', max: 30 },
  { key: 'vouches', label: 'What vouches say', max: 25 },
  { key: 'rotation', label: 'Rotation: category, class decade, area', max: 20 },
  { key: 'listing', label: 'Listing quality', max: 15 },
  { key: 'first_time', label: 'First-time feature', max: 10 },
]

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

/**
 * Per-criterion and total averages excluding recused scorers, rounded to 0.1;
 * null averages when nobody scored (every scorer recused, or no scores at
 * all). `botm_scores`' own `botm_scores_complete` check guarantees every
 * NON-recused row has every criterion filled in, which is why `?? 0` below is
 * safe — it's a defensive fallback for a column that complete rows never
 * leave null, not a stand-in for a recused scorer's blank columns. Recused
 * rows are filtered out by `active` before this ever runs.
 */
export function averageScores(
  rows: ScoreRow[],
): { byCriterion: Record<string, number | null>; total: number | null; scorers: number; recused: number } {
  const active = rows.filter((r) => !r.recused)
  const recused = rows.length - active.length

  if (active.length === 0) {
    const byCriterion: Record<string, number | null> = {}
    for (const c of RUBRIC) byCriterion[c.key] = null
    return { byCriterion, total: null, scorers: 0, recused }
  }

  const byCriterion: Record<string, number | null> = {}
  for (const c of RUBRIC) {
    const sum = active.reduce((acc, r) => acc + (r[c.key] ?? 0), 0)
    byCriterion[c.key] = round1(sum / active.length)
  }
  const total = round1(active.reduce((acc, r) => acc + r.total, 0) / active.length)

  return { byCriterion, total, scorers: active.length, recused }
}

/** The five spotlight interview questions, in order. */
export const STORY_QUESTIONS: string[] = [
  'What do you do, and who do you serve?',
  'How did MUAA shape you?',
  'What was your hardest season, and what carried you through?',
  'What win are you proudest of?',
  'How can alumni help?',
]
